<template>
    <rkm-modal
        extra-large
        ref="RkmModal"
        title="Procedimento administrativo"
    >
        <rkm-form
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="5">
                    <rkm-form-input-autocomplete
                        :disabled="!!item"
                        :repository="repositories.procedimento"
                        :request-query-string-params="requestQueryStringParams.procedimento"
                        :search-fields="fields.procedimento"
                        :text-field="(item) => `${item.origem === 'abpg' ? item.codigo : formatProcedimento(item.codigo)} - ${item.nome}`"
                        :validations="validations.procedimento"
                        @select="model.cid = null"
                        label="Procedimento"
                        v-model="model.procedimento"
                    />
                </rkm-col>

                <rkm-col cols="5">
                    <rkm-form-input-autocomplete
                        :disabled="!model.procedimento || isProcedimentoAbpg"
                        :repository="repositories.cid"
                        :request-query-string-params="requestQueryStringParams.cid"
                        :search-fields="fields.cid"
                        :text-field="(item) => `${formatCid(item.codigo)} - ${item.nome}`"
                        :validations="validations.cid"
                        label="CID"
                        v-model="model.cid"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        :disabled="isProcedimentoAbpg"
                        :validations="validations.quantidade"
                        label="Quantidade"
                        number
                        v-model="model.quantidade"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.observacao"
                        label="Observações"
                        rows="4"
                        v-model="model.observacao"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>

            <rkm-button
                @click="save"
                color="primary"
            >
                {{item ? 'Salvar' : 'Incluir'}}
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Enums.
import procedimento from '@/enums/sigtap/procedimento'
import procedimentoAtencaoBasica from '@/enums/atencao-basica/atendimento-odontologico/procedimento'

// Mixins.
import {formats, object, repository} from '@/mixins'

export default {
    computed: {
        requestQueryStringParams() {
            return {
                procedimento: {
                    cbo_lotacao_atual: true,
                    paciente_id: this.data.additionals.paciente.id,
                    competencia_id: this.data.additionals.isRetroativo ? this.data.additionals.agendamentoCompetenciaId : null,
                    with_codigo_ab: this.data.additionals.isAtendimentoSaudeBucal,
                },
                cid: this.model.procedimento && this.model.procedimento.hasCidPrincipal
                    ? {
                        principal: true,
                        procedimento_id: this.model.procedimento.id,
                    }
                    : {}
            }
        },
        isProcedimentoAbpg() {
            return !!(this.model.procedimento && this.model.procedimento.origem === 'abpg')
        },
        validations() {
            const validations = {
                procedimento: {required: true},
                cid: {required: this.model.procedimento && !!this.model.procedimento.hasCidPrincipal},
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.isProcedimentoAbpg
                        ? 1
                        : (this.data.additionals.isAtendimentoSaudeBucal ? this.getMaxValue() : 3),
                },
                observacao: {max: 500},
            }

            return validations
        },
    },
    created() {
        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
        }
    },
    data: () => ({
        enums: {procedimento, procedimentoAtencaoBasica},
        fields: {
            procedimento: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            cid: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
        },
        item: null,
        model: {
            procedimento: null,
            cid: null,
            quantidade: 1,
            observacao: null,
        },
    }),
    methods: {
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        create() {
            this.resetForm()
        },
        edit() {
            this.model = this.clone({
                procedimento: this.item.procedimento,
                cid: this.item.cid,
                quantidade: this.item.quantidade,
                observacao: this.item.observacao,
            })
        },
        store() {
            this.data.model.procedimentosAdministrativos.push({
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                    origem: this.model.procedimento.origem || 'sigtap',
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        update() {
            Object.assign(this.item, {
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                    origem: this.model.procedimento.origem || 'sigtap',
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    const isDuplicado = procedimento =>
                        procedimento.procedimento.id === this.model.procedimento.id
                        && (procedimento.procedimento.origem || 'sigtap') === (this.model.procedimento.origem || 'sigtap')

                    if (!this.item && this.data.model.procedimentosAdministrativos.find(isDuplicado)) {
                        this.showValidationNotification('O procedimento administrativo já foi incluído.')
                        return
                    }

                    if (this.isProcedimentoAbpg
                        && (this.data.procedimentosClinicosModel || []).find(isDuplicado)) {
                        this.showValidationNotification('Este código AB já foi incluído em Procedimento clínico.')
                        return
                    }

                    if (this.item) {
                        this.update()
                    } else {
                        this.store()
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.model.procedimento = null
            this.model.cid = null
            this.model.quantidade = 1
            this.model.observacao = null
        },
        getMaxValue() {
            if(this.model.procedimento) {
                if([
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.COLOCACAO_PLACA_MORDIDA,
                    this.enums.procedimentoAtencaoBasica.MANUTENCAOO_CONSERTO_APARELHO,
                    this.enums.procedimentoAtencaoBasica.MOLDAGEM_DENTO_GENGIVAL,
                    this.enums.procedimentoAtencaoBasica.EXERESE_CISTO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_FISTULA,
                    this.enums.procedimentoAtencaoBasica.CORRECAO_BRIDAS_MUSCULARES,
                    this.enums.procedimentoAtencaoBasica.CORRECAO_IRREGULARIDADES_REBORDO_ALVEOLAR,
                    this.enums.procedimentoAtencaoBasica.REMOCAO_TORUS_EXOSTOSES,
                    this.enums.procedimentoAtencaoBasica.CONTENCAOO_DENTES_SPLINTAGEM,
                    this.enums.procedimentoAtencaoBasica.REDUCAO_LUXACAOO_TEMPORO_MANDIBULAR,
                    this.enums.procedimentoAtencaoBasica.RETIRADA_MATERIAL_SÍNTESE,
                    this.enums.procedimentoAtencaoBasica.MANTENEDOR_ESPAÇO,
                    this.enums.procedimentoAtencaoBasica.PLACA_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.PLANO_INCLINADO,
                    this.enums.procedimentoAtencaoBasica.PROTESE_PARCIAL_MANDIBULAR_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.PROTESE_PARCIAL_MAXILAR_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TEMPORARIA,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TOTAL_MANDIBULAR,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TOTAL_MAXILAR,
                    this.enums.procedimentoAtencaoBasica.APARELHO_FIXO_BILATERAL_PARA_FECHAMENTO_DE_DIASTEMA,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTODONTICO_FIXO,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTOPEDICO_E_ORTODONTICO_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTOPEDICO_FIXO,
                    this.enums.procedimentoAtencaoBasica.APARELHO_PARA_BLOQUEIO_MAXILO_MANDIBULAR,
                ].includes(this.model.procedimento.codigo)) {
                    return 2
                }
                if([
                    this.enums.procedimentoAtencaoBasica.APLICACAO_CARIOSTATICO,
                    this.enums.procedimentoAtencaoBasica.APLICACAO_SELANTE,
                    this.enums.procedimentoAtencaoBasica.SELAMENTO_PROVISORIO_CAVIDADE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_INTERPROXIMAL,
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_PERIAPICAL,
                    this.enums.procedimentoAtencaoBasica.CAPEAMENTO_PULPAR,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_ANTERIOR,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_INICIAL_DENTE_TRAUMATIZADO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_RESTAURADOR_ATRAUMATICO,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTEDECIDUO_POSTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_POSTERIOR_AMALGAMA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_POSTERIOR_IONOMERO_VIDRO,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_ANTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_POSTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_POSTERIOR_AMALGAMA,
                    this.enums.procedimentoAtencaoBasica.ACESSO_POLPA_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.CURATIVO_DEMORA,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_DECIDUO,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_BIRRADICULAR,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_RAIZES,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_UNIRRADICULAR,
                    this.enums.procedimentoAtencaoBasica.PULPOTOMIA_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_BIRADICULAR,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_RAIZES,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_UNIRADICULAR,
                    this.enums.procedimentoAtencaoBasica.SELAMENTO_PERFURACAO_RADICULAR,
                    this.enums.procedimentoAtencaoBasica.MOLDAGEM_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.REEMBASAMENTO_CONSERTO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.CIMENTACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.ADAPTACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.AJUSTE_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.INSTALACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.APICECTOMIA,
                    this.enums.procedimentoAtencaoBasica.CURETAGEM_PERIAPICAL,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_DENTE_DECIDUO,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_DENTE_PERMANENTE,
                    this.enums.procedimentoAtencaoBasica.ODONTOSECCAO_RADILECTOMIA_TUNELIZACAO,
                    this.enums.procedimentoAtencaoBasica.REIMPLANTE_TRANSPLANTE_DENTAL,
                    this.enums.procedimentoAtencaoBasica.REMOCAO_DENTE_RETIDO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_TRACIONAMENTO_DENTAL,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_ALVEOLITE,
                    this.enums.procedimentoAtencaoBasica.ULOTOMIA_ULECTOMIA,
                    this.enums.procedimentoAtencaoBasica.COROA_PROVISORIA,
                    this.enums.procedimentoAtencaoBasica.PROTESE_CORONARIAS,
                    this.enums.procedimentoAtencaoBasica.COROA_DE_ACO_E_POLICARBOXILATO,
                    this.enums.procedimentoAtencaoBasica.PROTESE_DENTARIA_SOBRE_IMPLANTE,
                ].includes(this.model.procedimento.codigo)) {
                    return 32
                }
                if([
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_SUBGENGIVAIS,
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_CORONO_RADICULAR,
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_SUPRAGENGIVAIS,
                    this.enums.procedimentoAtencaoBasica.APROFUNDAMENTO_VESTIBULO_ORAL,
                    this.enums.procedimentoAtencaoBasica.GENGIVECTOMIA,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_MULTIPLA_ALVEOLOPLASTIA,
                    this.enums.procedimentoAtencaoBasica.GENGIVOPLASTIA,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_PERIODONTAL,
                ].includes(this.model.procedimento.codigo)) {
                    return 6
                }
            }
            return 1
        },
    },
    mixins: [formats, object, repository],
    props: {
        data: {required: true, type: Object},
    },
}
</script>